import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, DownloadSimple, Upload, TrashSimple, ClosedCaptioning } from "@phosphor-icons/react";
import api from "../lib/api";

const LANGS = ["en", "it"];

export default function TranscriptManagerModal({ content, onClose }) {
  const [status, setStatus] = useState(null); // { en: {count, source}, it: {...} }
  const [lang, setLang] = useState("en");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const loadStatus = () => {
    if (!content) return;
    // We reuse GET transcript to know what's cached — one call per supported lang
    const out = {};
    Promise.all(
      LANGS.map((L) =>
        api.get(`/content/${content.id}/transcript`, { params: { lang: L } })
          .then((r) => {
            if (r.data.status === "ok" && r.data.lang === L) {
              out[L] = { count: r.data.segments.length, source: r.data.source };
            }
          })
          .catch(() => {})
      )
    ).then(() => setStatus(out));
  };

  useEffect(() => { loadStatus(); }, [content?.id]); // eslint-disable-line

  if (!content) return null;

  const hasYouTube = !!content.youtube_url;

  const doFetch = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/content/${content.id}/transcript/fetch`);
      toast.success(`Fetched ${res.data.count} segments (${res.data.lang.toUpperCase()})`);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const doUpload = async () => {
    if (!text.trim()) { toast.error("Paste some transcript text first"); return; }
    setLoading(true);
    try {
      const res = await api.post(`/admin/content/${content.id}/transcript/upload`, { lang, text });
      toast.success(`Saved ${res.data.count} segments in ${res.data.lang.toUpperCase()}`);
      setText("");
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const doDelete = async (L) => {
    if (!window.confirm(`Delete ${L.toUpperCase()} transcript?`)) return;
    setLoading(true);
    try {
      await api.delete(`/admin/content/${content.id}/transcript`, { params: { lang: L } });
      toast.success(`Deleted ${L.toUpperCase()} transcript`);
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ""));
    r.readAsText(f);
  };

  return (
    <div
      data-testid="transcript-manager-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-cyan/30 bg-ink-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-white/10 px-6 py-4">
          <div className="flex items-center gap-3">
            <ClosedCaptioning size={22} weight="duotone" className="text-cyan" />
            <div>
              <p className="font-head text-sm font-bold uppercase tracking-widest text-white">Transcript</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {content.title.slice(0, 60)}
              </p>
            </div>
          </div>
          <button data-testid="tx-close" onClick={onClose} className="p-2 text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {/* Current status */}
          <div>
            <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Current status</p>
            {status === null && <p className="font-mono text-xs text-slate-500">Loading…</p>}
            {status && Object.keys(status).length === 0 && (
              <p className="font-mono text-xs text-slate-500">No transcript uploaded yet.</p>
            )}
            {status && Object.keys(status).length > 0 && (
              <div className="space-y-2">
                {Object.entries(status).map(([L, info]) => (
                  <div key={L} className="flex items-center justify-between border border-white/10 px-3 py-2">
                    <div>
                      <span className="font-mono text-xs font-bold uppercase tracking-widest text-cyan">{L}</span>
                      <span className="ml-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        {info.count} segments · {info.source}
                      </span>
                    </div>
                    <button
                      data-testid={`tx-delete-${L}`}
                      onClick={() => doDelete(L)}
                      disabled={loading}
                      className="p-2 text-slate-400 hover:text-red-400"
                    >
                      <TrashSimple size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auto-fetch from YouTube */}
          {hasYouTube && (
            <div className="border border-white/10 bg-ink-900/40 p-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-cyan">1. Auto-fetch from YouTube</p>
              <p className="mb-3 font-body text-xs text-slate-400">
                Try to grab the auto-generated captions for this YouTube video. Requires the video to have captions.
              </p>
              <button
                data-testid="tx-fetch"
                onClick={doFetch}
                disabled={loading}
                className="flex items-center gap-2 bg-cyan px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white disabled:opacity-50"
              >
                <DownloadSimple size={14} weight="bold" /> Fetch from YouTube
              </button>
            </div>
          )}

          {/* Manual upload / paste */}
          <div className="border border-white/10 bg-ink-900/40 p-4">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
              {hasYouTube ? "2. " : ""}Upload manually (VTT / SRT / mm:ss text)
            </p>
            <p className="mb-3 font-body text-xs text-slate-400">
              Format: one line per segment, starting with <span className="font-mono text-cyan">mm:ss</span> (e.g.
              <span className="font-mono text-slate-300"> 00:15 Hello everyone</span>). Also accepts SRT and WEBVTT.
            </p>

            <div className="mb-3 flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Language:</span>
              {LANGS.map((L) => (
                <button
                  key={L}
                  data-testid={`tx-lang-${L}`}
                  onClick={() => setLang(L)}
                  className="border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors"
                  style={{
                    borderColor: lang === L ? "#00F0FF" : "rgba(255,255,255,0.15)",
                    backgroundColor: lang === L ? "rgba(0,240,255,0.12)" : "transparent",
                    color: lang === L ? "#00F0FF" : "#94A3B8",
                  }}
                >
                  {L.toUpperCase()}
                </button>
              ))}
              <label className="ml-auto flex cursor-pointer items-center gap-2 border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-cyan hover:text-cyan">
                <Upload size={12} /> Load file
                <input type="file" accept=".txt,.vtt,.srt" onChange={onFile} className="hidden" />
              </label>
            </div>

            <textarea
              data-testid="tx-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"00:01 Buongiorno a tutti\n00:05 Oggi parliamo di..."}
              rows={10}
              className="w-full border border-white/15 bg-ink-900/60 px-3 py-2 font-mono text-xs text-slate-200 outline-none focus:border-cyan"
            />
            <button
              data-testid="tx-save"
              onClick={doUpload}
              disabled={loading || !text.trim()}
              className="mt-3 bg-cyan px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white disabled:opacity-50"
            >
              Save transcript
            </button>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600">
            Note: uploading in one language automatically enables translation to the other supported language on demand.
          </p>
        </div>
      </div>
    </div>
  );
}
